import { useState, useMemo, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import _ from 'lodash';
import { InputBase, Box, IconButton, ButtonBase, Paper, Popover, FormControlLabel } from '@material-ui/core';
import { SearchIcon, DropDownIcon, DropUpIcon, CheckIcon } from '../../../../components/Icon';
import { i18n } from '../../../../language';
import { classNames, getArrayRandomKey, onKeyUpEnterOrSpace } from '../../../../utils';
import ehandoverIds from '../../../../ids/Ehandover';
import { AutoCompleteSelect } from '../../../../components';
import StyleButton from '../../../../components/StyleButton';
import ascIcon from '../../../../static/images/ascIcon.png';
import descIcon from '../../../../static/images/descIcon.png';
import { WarpSwitch } from '../../../../components/WarpSwitch';
import DateCom from './date';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    backgroundColor: theme.palette.font.white,
    padding: '1.205rem 1.875rem 0.977rem 1.875rem',
    borderBottom: '0.0625rem solid #ddd',
    borderRadius: '0 0.9375rem 0 0',
    minWidth: '31.25rem',
    gridGap: '1.142rem',
    flexDirection: 'column',
  },
  search: {
    borderRadius: '0.5rem',
    backgroundColor: theme.palette.font.white,
    width: '18.5rem',
    height: '2.375rem',
    display: 'flex',
    justifyContent: 'space-between',
    border: '0.0625rem solid #ddd',
  },
  input: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.919rem',
    letterSpacing: '0',
    color: theme.palette.font.sub,
    fontWeight: '500',
    margin: '0 0.938rem',
    width: '100%',
  },
  iconbtn: {
    padding: '0',
  },
  icon: {
    backgroundColor: '#4c93ff',
    color: theme.palette.font.white,
    padding: '0.5rem 0.5rem 0.438rem 0.438rem',
    borderRadius: '0.4875rem',
    fontSize: '1.875rem',
    margin: '0.313rem 0.313rem 0.313rem 0',
  },
  tagRoot: ({ width }) => ({
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    letterSpacing: 0,
    color: '#c0c6cb',
    fontWeight: 500,
    width: width ?? '16.563rem',
    height: '2.5rem',
    borderRadius: '8px',
    border: 'solid 1px #e4e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 0.75rem 0 0.938rem',
    whiteSpace: 'nowrap',
    '&:hover': {
      cursor: 'pointer',
    },
  }),
  dropDownIconBody: {
    widht: '1.5rem',
    height: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dropDownIcon: {
    fontSize: '0.875rem',
    color: theme.palette.primary.main,
  },
  paper: ({ width }) => ({
    // width: width ?? '16.563rem',
    minWidth: width ?? '16.563rem',
    overflow: 'auto',
    '&>div': {
      borderBottom: 'solid 1px #e4e7eb',
    },
    '&>div:last-child': {
      border: 'none',
    },
  }),
  tagTip: ({ itemHeight }) => ({
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: theme.palette.font.sub,
    fontWeight: 500,
    height: itemHeight ?? '3.125rem',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '0.938rem',
  }),
  selectItem: ({ itemHeight }) => ({
    height: itemHeight ?? '3.125rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: theme.palette.font.main,
    fontWeight: 500,
    display: 'flex',
    gridGap: '0.5rem',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 0.938rem',
    '&:hover': {
      cursor: 'pointer',
    },
  }),
  box: {
    width: '1.5rem',
    height: '1.5rem',
    borderRadius: '5px',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  checkbox: {
    border: 'solid 1px #c0c6cb',
  },
  checkedbox: {
    backgroundColor: '#28d2ab',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkIcon: {
    color: theme.palette.font.white,
    fontSize: '0.813rem',
  },
  resetButton: {
    backgroundColor: '#538fff',
    border: 'unset',
    '&.Mui-disabled': {
      backgroundColor: '#c0c6cb',
    },
  },
  quickDaysBtn: {
    border: '1px solid #E4E7EB',
    borderRadius: '0.571rem',
    padding: '0.5rem 0.857rem',
    fontSize: '1.142rem',
    fontFamily: 'NotoSans-Medium',
    cursor: 'pointer',
  },
  quickBtnActive: {
    color: '#538FFF',
    background: 'linear-gradient(0deg, #F5FAFF 0%, #F5FAFF 100%), #28D2AB',
    border: '1px solid #538FFF',
  },
  filterBox: {
    display: 'flex',
    // justifyContent: 'space-between',
    alignItems: 'center',
    gridGap: '0.5rem',
    flexWrap: 'wrap',
  },
  flexSpace: {
    display: 'block',
    minWidth: 0,
    flex: 1,

    [`@media (max-width: 1170px)`]: {
      display: 'none',
    },
  },
  acknowledgeBox: {
    margin: '0',

    '& .MuiFormControlLabel-label': {
      marginLeft: '0.571rem',
      fontFamily: 'NotoSans-Medium',
      color: '#2e4469',
      fontWeight: '500',
      fontSize: '1rem',
    },
  },
}));

const CheckBox = ({ checked }) => {
  const classes = useStyles();
  return (
    <div className={checked ? classNames(classes.box, classes.checkedbox) : classNames(classes.box, classes.checkbox)}>
      {checked && <CheckIcon className={classes.checkIcon} />}
    </div>
  );
};

const SelectItem = ({ id, classes, handleSelect, tagItem, selectedTags }) => {
  const selected = _.some(selectedTags, (item) => item?.id === tagItem?.id);
  return (
    <div
      id={id}
      className={classes.selectItem}
      onClick={() => handleSelect(tagItem)}
      tabIndex={0}
      onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleSelect(tagItem))}
    >
      {tagItem.text}
      <CheckBox checked={selected} />
    </div>
  );
};

const TagFilter = ({ placeholder, selectTip, tagList, selectedTags, onChange, width, itemHeight }) => {
  const classes = useStyles({ width, itemHeight });
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchText, setSearchText] = useState('');
  const filterList = useMemo(() => {
    const list = Array.isArray(tagList) ? [...tagList] : [];
    if (!searchText.trim()) return list;
    const lowerKeyword = searchText.toLowerCase();
    return list?.filter((item) => {
      const value = item?.text;
      return value && value.toString().toLowerCase().includes(lowerKeyword);
    });
  }, [tagList, searchText]);

  const handleClick = (event) => {
    if (!anchorEl) {
      setAnchorEl(event.currentTarget);
      return;
    }
    setAnchorEl(null);
  };

  return (
    <>
      <ButtonBase
        id={ehandoverIds.filter + 1}
        className={classes.tagRoot}
        onClick={handleClick}
        style={selectedTags.length > 0 ? { color: '#2e4469' } : {}}
      >
        {selectedTags.length > 0 ? i18n.t('EHANDOVER_TAG_SELECTED', { count: selectedTags.length }) : placeholder}
        <div className={classes.dropDownIconBody}>
          {anchorEl ? (
            <DropUpIcon className={classes.dropDownIcon} />
          ) : (
            <DropDownIcon className={classes.dropDownIcon} />
          )}
        </div>
      </ButtonBase>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={() => setAnchorEl(null)}
      >
        <Paper className={classes.paper}>
          {/* {selectTip && <div className={classes.tagTip}>{selectTip}</div>} */}
          {selectTip && (
            <InputBase
              id={ehandoverIds.filter + 2}
              className={classes.input}
              value={searchText}
              onChange={(event) => setSearchText(event?.target?.value)}
              placeholder={selectTip}
              onKeyUp={(e) => onKeyUpEnterOrSpace(e, () => setSearchText(e?.target?.value))}
              style={{ margin: 0, padding: '0 0.938rem', height: '3.5rem' }}
            />
          )}
          {filterList.map((item, index) => {
            return (
              <SelectItem
                id={ehandoverIds.tag_filter + index}
                key={getArrayRandomKey(index, `${item?.text}_${item?.id}`)}
                classes={classes}
                handleSelect={onChange}
                tagItem={item}
                selectedTags={selectedTags}
              />
            );
          })}
        </Paper>
      </Popover>
    </>
  );
};

export default function Search({
  handleSubmitSearch,
  setEndDate,
  setStartDate,
  search,
  type,
  startDate,
  endDate,
  tagList,
  selectedTags,
  setSelectedTags,
  followUpFilter,
  handleFollowUpFilter,
  followUpTotal,
  handleReset,
  quickDays,
  setQuickDays,
  sortOrder,
  setSortOrder,
  requiredAcknowLedge,
  setRequiredAcknowLedge,
  setHideCompletedItem,
  isHideCompletedItem,
  showHideCompleteItem = false,
}) {
  const classes = useStyles();
  const isAscOrder = sortOrder?.DATE_START === 'asc';
  const [searchDraft, setSearchDraft] = useState(search);

  useEffect(() => {
    setSearchDraft(search);
  }, [search]);

  const isEmptyPickerInput = (value) => {
    return value === null || value === '' || value?.['_i'] === '';
  };

  const isIncompletePickerInput = (value) => {
    return typeof value?.['_i'] === 'string' && value?.['_i']?.includes('_');
  };

  const handleSelectTag = (tagItem) => {
    const isSelected = _.some(selectedTags, (item) => item?.id === tagItem?.id);
    setSelectedTags((prev) => {
      return isSelected ? _.filter(prev, (item) => item?.id !== tagItem?.id) : [...prev, tagItem];
    });
  };

  const handleSetStartDate = (props) => {
    // 手动输入日期时，半成品值先留在日期控件内部，
    // 不要立刻把页面级 startDate 改成 null，否则会把整页筛选状态一起带着重算。
    if (isIncompletePickerInput(props)) {
      return;
    }
    if (isEmptyPickerInput(props)) {
      setStartDate(null);
      return;
    }
    if (!moment(props).isValid()) {
      return;
    }
    setStartDate(moment(props).format('yyyy-MM-DD 00:00'));
  };

  const handleSetEndDate = (props) => {
    // endDate 也保持同样策略：输入过程中的半成品不往上同步，
    // 只有清空、合法输入或面板选择完成后才真正更新页面级筛选值。
    if (isIncompletePickerInput(props)) {
      return;
    }
    if (isEmptyPickerInput(props)) {
      setEndDate(null);
      return;
    }
    if (!moment(props).isValid()) {
      return;
    }
    if (
      (type === 'task' || type === 'followUp') &&
      moment(props).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')
    ) {
      setEndDate(moment().format('yyyy-MM-DD HH:mm'));
      return;
    }
    setEndDate(moment(props).format('yyyy-MM-DD 23:59'));
  };

  const handleFilterByDate = (val, type) => {
    setQuickDays('');
    if (type === 'START') {
      handleSetStartDate(val);
    } else {
      handleSetEndDate(val);
    }
  };

  const followupItemList = useMemo(
    () =>
      _.map(followUpTotal, (item) => ({
        text: item.value === '2' ? `Incomplete (${item.text})` : `Completed (${item.text})`,
        value: item.value,
      })),
    [followUpTotal]
  );
  const quickFilterDays = [
    {
      text: i18n.t('EHANDOVER_DAYS_3'),
      unit: 'days',
      num: 3,
    },
    {
      text: i18n.t('EHANDOVER_DAYS_7'),
      unit: 'days',
      num: 7,
    },
    {
      text: i18n.t('EHANDOVER_MONTH_1'),
      unit: 'months',
      num: 1,
    },
    {
      text: i18n.t('EHANDOVER_YEAR_1'),
      unit: 'years',
      num: 1,
    },
  ];
  const handleClickQuickDay = (item) => {
    setQuickDays(item?.text);
    const startDate = moment().subtract(item?.num, item?.unit);
    handleSetEndDate(moment());
    handleSetStartDate(startDate);
  };
  const handleSortOrder = () => {
    const dateOrder = isAscOrder ? 'desc' : 'asc';
    setSortOrder((prev) => ({
      ...prev,
      DATE_START: dateOrder,
    }));
  };
  const submitSearch = useCallback(() => {
    handleSubmitSearch(searchDraft);
  }, [handleSubmitSearch, searchDraft]);

  const innerHandleFollowUpFilter = useCallback(
    (value) => {
      if(value !== '2'){ // if change search status to complete
        setHideCompletedItem(false);
      }
      handleFollowUpFilter(value);
    }
  ,[handleFollowUpFilter,setHideCompletedItem]);

  return (
    <div className={classes.root}>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap="0.5rem">
        <div style={{ display: 'flex', gridGap: '1.25rem' }}>
          <Box className={classes.search}>
            <InputBase
              id={ehandoverIds.filter + 2}
              className={classes.input}
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              placeholder={i18n.t('EHANDOVER_SEARCH')}
              onKeyDown={(event) => {
                if (event.code === 'Enter' || event.code === 'NumpadEnter') {
                  submitSearch();
                }
              }}
            />
            <IconButton id={ehandoverIds.filter + 3} className={classes.iconbtn} onClick={submitSearch}>
              <SearchIcon className={classes.icon} />
            </IconButton>
          </Box>
          <TagFilter
            placeholder={i18n.t('EHANDOVER_TAG_PLACEHOLDER')}
            selectTip={i18n.t('EHANDOVER_TAG_TIP')}
            tagList={tagList}
            selectedTags={selectedTags}
            onChange={handleSelectTag}
          />
          {type === 'followUp' && (
            <AutoCompleteSelect
              height="2.5rem"
              itemList={followupItemList}
              value={followUpFilter}
              onChange={innerHandleFollowUpFilter}
            />
          )}
          {type !== 'notification' && (
            <FormControlLabel
              control={
                <WarpSwitch
                  checked={requiredAcknowLedge}
                  onChange={(event) => setRequiredAcknowLedge(event.target.checked)}
                  color="primary"
                />
              }
              label={i18n.t('EHANDOVER_REQUIRE_ACKNOWLEDGE')}
              className={classes.acknowledgeBox}
            />
          )}
        </div>
        <StyleButton
          className={classes.resetButton}
          color="#fff"
          width="8rem"
          id="ehandover_reset_search_button"
          text={i18n.t('EHANDOVER_RESET_SEARCH')}
          onClick={handleReset}
        />
      </Box>
      <Box height="1px" bgcolor="#E4E7EB" width="100%" />
      <Box className={classes.filterBox}>
        <Box fontSize="1.142rem" fontFamily="NotoSans-Medium">
          {i18n.t('EHANDOVER_QUICK_FILTER')}
        </Box>
        {_.map(quickFilterDays, (item, index) => (
          <Box
            className={classNames(classes.quickDaysBtn, quickDays === item?.text && classes.quickBtnActive)}
            onClick={() => handleClickQuickDay(item)}
            key={getArrayRandomKey(index, 'quick-filter-days')}
          >
            {item?.text}
          </Box>
        ))}
        {showHideCompleteItem && (
          <FormControlLabel
          control={
            <WarpSwitch
              checked={isHideCompletedItem}
              onChange={(event) => (setHideCompletedItem(event.target.checked))}
              color="primary"
            />
          }
          label={i18n.t('EHANDOVER_HIDE_COMPLETED_ITEMS')}
          className={classes.acknowledgeBox}
          />)
        }
        <Box className={classes.flexSpace} />
        <DateCom
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          type={type}
          startDate={startDate}
          endDate={endDate}
          handleSetStartDate={(val) => handleFilterByDate(val, 'START')}
          handleSetEndDate={(val) => handleFilterByDate(val, 'END')}
        />
        <Box
          display="flex"
          gridGap="4px"
          className={classNames(classes.quickDaysBtn, classes.quickBtnActive)}
          onClick={handleSortOrder}
        >
          <Box width="1.714rem" height="1.714rem">
            <img src={isAscOrder ? ascIcon : descIcon} alt="sort icon" width="100%" />
          </Box>
          <Box>{isAscOrder ? i18n.t('EHANDOVER_ASC') : i18n.t('EHANDOVER_DESC')}</Box>
        </Box>
      </Box>
    </div>
  );
}
